import React, { useContext, useState } from "react";
import useT from "../../hooks/useT";
import { APP_PAGE, actions } from "../../hooks/useAppState";
import Modal from "../Modal";
import Button from "../Button";
import CustomerDetails from "../CustomerDetails";
import { ReactSelectOptionObject } from "../InputSelectCustom";
import { AppStateContext } from "../../containers/App";
import { get } from "../../utils/helpers";
import {
  ExistingCustomerDataStateObject,
  setCustomerDataState
} from "../../hooks/useCustomerDataState";
//import debug from "../../utils/debug";
import "./CustomersTable.css";

export interface CustomersTableProps {
  customers: ExistingCustomerDataStateObject[];
  columns: CustomersTableColumnDefinition[];
  detailsColumns: string[];
  enableEdit?: boolean;
}

export interface CustomersTableColumnDefinitionObject {
  name: string;
  label: string;
}

export type CustomersTableColumnDefinition =
  | ReactSelectOptionObject
  | ReactSelectOptionObject[]
  | string
  | string[];

export interface CustomerTableRow {
  key: string;
  customer: ExistingCustomerDataStateObject;
  columns: CustomersTableColumnValue[];
}
export interface CustomersTableColumnValue {
  label: string;
  name: string;
  value: any;
  secondaryValue: any;
}

export default function CustomersTable({
  customers,
  columns,
  detailsColumns,
  enableEdit
}: CustomersTableProps) {
  const { t } = useT();
  const [, doAppStateAction] = useContext(AppStateContext);
  const [showCustomerDetails, setshowCustomerDetails] = useState<boolean>(
    false
  );
  const [selectedCustomer, setCustomer] = useState<
    ExistingCustomerDataStateObject
  >();
  //debug("[CustomersTable] customers:", customers);
  //debug("[CustomersTable] columns:", columns);

  const view = (customer: ExistingCustomerDataStateObject) => {
    //debug("[CustomersTable] ", customer);
    setshowCustomerDetails(true);
    setCustomer(customer);
  };

  const closeModal = () => {
    setshowCustomerDetails(false);
  };

  const edit = (customer: ExistingCustomerDataStateObject) => {
    //debug("[CustomersTable] ", customer);
    setCustomerDataState(customer);
    doAppStateAction(actions.setPage(APP_PAGE.CUSTOMER));
  };

  const rows: CustomerTableRow[] = customers.map(customer => ({
    key: customer.identifier || "",
    customer: customer,
    columns: columns.map((column: CustomersTableColumnDefinition) => {
      let secondaryValue;
      if (Array.isArray(column) && column.length) {
        if (column.length > 1) {
          const secondaryField: string =
            typeof column[1] == "object" ? get(column[1], "value") : column[1];
          secondaryValue = customer[secondaryField];
        }
        column = column[0];
      }
      const columnValue: CustomersTableColumnValue =
        typeof column === "object"
          ? {
              label: get(column, "label"),
              name: get(column, "value"),
              value: customer[get(column, "value")],
              secondaryValue: secondaryValue
            }
          : {
              label: "customer.data.fields." + column + ".label",
              name: column,
              value: customer[column],
              secondaryValue: secondaryValue
            };
      return columnValue;
    })
  }));

  return (
    <div className="customers-container">
      <div className="customers-list">
        {customers.length > 0 &&
          rows.map(row => (
            <div className="customer-card" key={row.key}>
              <div className="customer-row">
                {row.columns.map((column: CustomersTableColumnValue) => (
                  <div key={column.label} className="customr-col">
                    <div className={column.name}>
                      <div className="label">{t(column.label, column)}</div>
                      <div className="value">{column.value}</div>
                      {column.secondaryValue && (
                        <div className="value secondary">
                          {column.secondaryValue}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="actions-row">
                  <div className="buttons">
                    <Button
                      label={t("general.buttons.view.label", "View")}
                      shape="solid"
                      theme="primary"
                      onClick={() => view(row.customer)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
      {showCustomerDetails && selectedCustomer && (
        <Modal className="customer-details" isOpen={true} allowClose={true}>
          <header className="customer-details-header">
            <h2 className="customer-details-title section-header-title">
              {t("search.customerDetails.label", "Edit")}
            </h2>

            <Button
              className="button-role-close"
              icon="close"
              ariaLabel={t("general.buttons.close.label")}
              onClick={closeModal}
            />
          </header>
          <div>
            <CustomerDetails
              customer={selectedCustomer}
              columns={detailsColumns}
              t={t}
            />
            {enableEdit && (
              <Button
                type="submit"
                shape="solid"
                theme="primary"
                label={t("general.buttons.edit.label", "Edit")}
                className="button-role-submit"
                onClick={() => edit(selectedCustomer)}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
